<template>
  <QuestionPadding>
    <HintText class="video-recorder-controls__video-message">
      {{ isReviewing ? c('video_question.review_answer') : c('video_question.record_answer') }}
    </HintText>
    <ScrollIntersectionObserver>
      <VideoRecorderWithControls
        :key="recorderKey"
        data-test-id="VideoQuestion"
        :error-message="errorMessage"
        @recorded="handleVideoRecorded"
        @reset="handleVideoReset"
        @error-starting-recording="handleErrorStartingRecording"
      />
      <!-- eslint-disable vue/no-v-html -->
      <div class="video-question__prompt" v-html="promptHtml" />
      <!-- eslint-enable vue/no-v-html -->
    </ScrollIntersectionObserver>
  </QuestionPadding>
</template>
<script lang="ts">
import { computed, type DeepReadonly, defineComponent, type PropType } from 'vue'

import { createEmptyAnswer } from '../../../answer/factories/Answer'
import { type Answer } from '../../../answer/models/Answer'
import { parseUserMarkdown } from '../../../interview/utils/parseUserMarkdown'
import { c } from '../../../intl'
import HintText from '../../../question/components/dumb/HintText.vue'
import QuestionPadding from '../../../question/components/dumb/QuestionPadding.vue'
import { type Question } from '../../../question/models/Question'
import ScrollIntersectionObserver from '../../../shared/components/dumb/ScrollIntersectionObserver.vue'

import VideoRecorderWithControls from './VideoRecorderWithControls.vue'

export default defineComponent({
  components: {
    QuestionPadding,
    ScrollIntersectionObserver,
    VideoRecorderWithControls,
    HintText,
  },
  props: {
    question: {
      type: Object as PropType<DeepReadonly<Pick<Question<'video'>, 'prompt' | 'fields'>>>,
      required: true,
    },
    errorMessage: {
      type: String,
      required: false,
      default: undefined,
    },
    answer: {
      type: Object as PropType<DeepReadonly<Answer | undefined>>,
      required: true,
    },
    setAnswer: {
      type: Function as PropType<(value: { fieldId: string; value: Answer['value'] }) => void>,
      required: true,
    },
    handleErrorStartingRecording: {
      type: Function as PropType<(_error: unknown) => void>,
      required: true,
    },
  },
  emits: {
    error: (_errorMessage: string) => true,
  },
  setup(props, ctx) {
    const isReviewing = computed(() => {
      return props.answer?.type === 'video' && props.answer.value.videoBlob !== undefined
    })
    const recorderKey = computed(() => props.answer?.id)

    const videoField = computed(() => {
      const field = props.question.fields.find((field) => field.type === 'video')
      if (!field) throw TypeError("Could not find field of type 'video'")
      return field
    })

    const handleVideoRecorded = (videoBlob: Blob): void => {
      if (videoBlob.size === 0) {
        ctx.emit('error', c('video_question.empty_video_error'))
        return
      }
      props.setAnswer({
        fieldId: videoField.value.id,
        value: { videoBlob },
      })
    }

    const handleVideoReset = (): void => {
      props.setAnswer(createEmptyAnswer({ id: videoField.value.id, type: 'video' }))
    }

    return {
      isReviewing,
      handleVideoRecorded,
      handleVideoReset,
      c,
      recorderKey,

      promptHtml: computed(() => parseUserMarkdown(props.question.prompt, { enabled: ['list'] })),
    }
  },
})
</script>
<style lang="postcss">
.video-question__prompt {
  margin-top: var(--attest-spacing-5);
  line-height: 1.4em;
  text-align: left;
}

.scroll-intersection-observer {
  padding-right: 1rem;
  margin-right: -1rem;
}
</style>
